<template>
  <div class="course-detail" v-loading="loading">
    <div class="page-toolbar">
      <div class="page-actions">
        <el-button plain round size="small" icon="el-icon-back" @click="goList"
          >返回课程列表</el-button
        >
      </div>
    </div>

    <!-- 课程详情 -->
    <el-card class="section-card" shadow="never">
      <div slot="header" class="section-header">
        <div class="section-title">
          <img class="title-icon-img" :src="icons.sectionDetail" alt="" />
          <span>课程详情</span>
        </div>
      </div>
      <div class="course-info" v-if="!loading">
        <div class="course-cover">
          <img v-if="coverImage" :src="coverImage" alt="课程封面" />
          <div v-else class="course-cover__placeholder">
            <i class="el-icon-picture-outline"></i>
            <span>暂无封面</span>
          </div>
        </div>
        <div class="course-meta">
          <div class="course-meta__title-row">
            <h2 class="course-meta__name">{{ courseDetail.courseName }}</h2>
            <el-tag size="small" :type="courseStatus.type" round>
              {{ courseStatus.label }}
            </el-tag>
          </div>
          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">类型</span>
              <span class="meta-value">
                <dict-tag
                  :type="DICT_TYPE.COURSE_TYPE"
                  :value="courseDetail.courseType"
                  :tag="false"
                />
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">分类</span>
              <span class="meta-value">{{ categoryText }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">学习路径</span>
              <span class="meta-value">
                <dict-tag
                  :type="DICT_TYPE.LEARNING_SYSTEM"
                  :value="courseDetail.learningSystem"
                  :tag="false"
                />
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">职业路径</span>
              <span class="meta-value meta-value--ellipsis">
                <el-tooltip
                  :content="pathTooltip"
                  placement="top"
                  :disabled="!pathTooltip || pathTooltip === '-'"
                >
                  <span class="meta-value__text">{{ pathTooltip }}</span>
                </el-tooltip>
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">推荐分类</span>
              <span class="meta-value meta-value--ellipsis">
                <el-tooltip
                  :content="recommendTooltip"
                  placement="top"
                  :disabled="!recommendTooltip || recommendTooltip === '-'"
                >
                  <span class="meta-value__text">{{ recommendTooltip }}</span>
                </el-tooltip>
              </span>
            </div>
            <div class="meta-item">
              <span class="meta-label">课时</span>
              <span class="meta-value"
                >{{ courseDetail.lessonCount || 0 }}课时</span
              >
            </div>
            <div class="meta-item">
              <span class="meta-label">主讲教师</span>
              <span class="meta-value">{{
                courseDetail.speakerName ||
                  courseDetail.lecturerName ||
                  courseDetail.speakerCode ||
                  "-"
              }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">负责教师</span>
              <span class="meta-value">{{
                courseDetail.managerName || courseDetail.managerCode || "-"
              }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">发布者</span>
              <span class="meta-value">{{
                courseDetail.publisher || "-"
              }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">发布时间</span>
              <span class="meta-value">{{
                formatDateTime(courseDetail.createTime, "YYYY-MM-DD") || "-"
              }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">开结课时间</span>
              <span class="meta-value"
                >{{
                  formatDateTime(courseDetail.startTime, "YYYY-MM-DD") || "-"
                }}
                ~
                {{
                  formatDateTime(courseDetail.endTime, "YYYY-MM-DD") || "-"
                }}</span
              >
            </div>
            <div class="meta-item">
              <span class="meta-label">上下架</span>
              <span class="meta-value"
                >{{
                  formatDateTime(courseDetail.shelfStartTime, "YYYY-MM-DD") ||
                    "-"
                }}
                ~
                {{
                  formatDateTime(courseDetail.shelfEndTime, "YYYY-MM-DD") || "-"
                }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 课程简介 -->
    <el-card class="section-card" shadow="never">
      <div slot="header" class="section-header">
        <div class="section-title">
          <img class="title-icon-img" :src="icons.sectionIntro" alt="" />
          <span>课程简介</span>
        </div>
      </div>
      <div class="course-introduce" v-if="courseDetail.courseIntroduce">
        <div
          class="course-introduce__html"
          v-html="courseDetail.courseIntroduce"
        ></div>
      </div>
      <div class="course-introduce" v-else>暂无简介</div>
    </el-card>

    <!-- 课程章节 -->
    <el-card
      class="section-card"
      shadow="never"
      v-if="!loading && courseDetail.courseCode"
    >
      <div slot="header" class="section-header">
        <div class="section-title">
          <img class="title-icon-img" :src="icons.sectionChapter" alt="" />
          <span>课程章节</span>
        </div>
        <span class="section-extra">共{{ chapterCount }}章</span>
      </div>
      <CourseList
        :courseCode="courseDetail.courseCode"
        :isEdit="false"
        @loaded="onChapterLoaded"
      />
    </el-card>

    <!-- 课程资料 -->
    <el-card
      class="section-card"
      shadow="never"
      v-if="!loading && courseDetail.courseCode"
    >
      <div slot="header" class="section-header">
        <div class="section-title">
          <img class="title-icon-img" :src="icons.sectionWare" alt="" />
          <span>课程资料</span>
        </div>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-plus"
          @click="openWareUpload"
          >上传资料</el-button
        >
      </div>
      <CourseWare
        ref="courseWareRef"
        :courseCode="courseDetail.courseCode"
        :courseData="courseDetail"
        :isEdit="false"
        :showDownload="true"
        :hideToolbar="true"
      />
    </el-card>

    <!-- 学员名单 -->
    <el-card class="section-card" shadow="never">
      <div slot="header" class="section-header">
        <div class="section-title">
          <img class="title-icon-img" :src="icons.sectionMember" alt="" />
          <span>学员名单</span>
        </div>
        <el-tag type="success">共{{ membersTotal }}个学员</el-tag>
      </div>
      <div class="member-filter">
        <el-input
          v-model.trim="membersParams.memberName"
          placeholder="请输入学员姓名"
          clearable
          prefix-icon="el-icon-search"
          class="member-filter__input"
          @keyup.enter.native="handleMemberSearch"
        />
        <el-button size="small" type="primary" @click="handleMemberSearch"
          >搜索</el-button
        >
      </div>
      <el-table
        :data="pagedMembers"
        style="width: 100%"
        class="member-table"
        header-cell-class-name="table-header"
      >
        <el-table-column type="index" label="序号" width="70" align="center" />
        <el-table-column prop="name" label="姓名" min-width="100" />
        <el-table-column prop="phone" label="手机号" min-width="130" />

        <el-table-column label="学习进度" min-width="200">
          <template slot-scope="scope">
            <div class="progress-cell">
              <el-progress
                :percentage="Number(scope.row.learningProgress) || 0"
                :stroke-width="8"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="加入时间" min-width="120" align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createTime, "YYYY-MM-DD") }}
          </template>
        </el-table-column>
      </el-table>
      <div class="member-pagination" v-if="membersTotal > 0">
        <el-pagination
          background
          layout="prev, pager, next"
          :current-page.sync="memberPage.pageNum"
          :page-size="memberPage.pageSize"
          :total="membersTotal"
          @current-change="handleMemberPageChange"
        />
      </div>
    </el-card>

    <!-- 关联题库 -->
    <div class="question-bank-section" v-if="questionBanks.length">
      <el-row :gutter="16">
        <el-col
          :xs="24"
          :sm="12"
          :md="12"
          :lg="6"
          v-for="item in questionBanks"
          :key="item.questionBankId"
        >
          <div class="bank-card">
            <div class="bank-card__title">{{ item.questionBankName }}</div>
            <div class="bank-card__course">
              关联课程: {{ courseDetail.courseName || "-" }}
            </div>
            <div class="bank-card__stats">
              <div class="stat-item">
                <div class="stat-value">{{ item.questionCount || 0 }}</div>
                <div class="stat-label">试题数量</div>
              </div>
              <div class="stat-item">
                <div class="stat-value">{{ item.questionTypeCount || 0 }}</div>
                <div class="stat-label">题型</div>
              </div>
              <div class="stat-item">
                <div class="stat-value">
                  {{ formatDateTime(item.createTime, "YYYY-MM-DD") || "-" }}
                </div>
                <div class="stat-label">创建时间</div>
              </div>
            </div>
            <div class="bank-card__footer">
              <el-button
                size="small"
                type="primary"
                @click="goAddQuestion(item)"
                >预览</el-button
              >
              <el-button
                plain
                round
                size="small"
                type="danger"
                @click="removeQuestionBank(item)"
                >移除</el-button
              >
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { getCourseMembers, getCourseDetail } from "@/api/course";
import {
  getCourseQuestionBankList,
  deleteQuestionBank
} from "@/api/questionBank";
import CourseList from "@/views/CourseEdit/CourseList";
import CourseWare from "@/views/CourseEdit/CourseWare";
import { formatDateTime } from "@/utils/util";
import { ensureDictCache, DICT_TYPE, getDictLabel } from "@/utils/dict";

export default {
  components: {
    CourseWare,
    CourseList
  },
  data() {
    return {
      DICT_TYPE,
      loading: false,
      courseCode: "",
      courseDetail: {},
      courseMembers: [],
      filteredMembers: [],
      chapterCount: 0,
      questionBanks: [],
      icons: {
        sectionDetail: require("@/assets/img/course-detail/section-detail.png"),
        sectionIntro: require("@/assets/img/course-detail/section-intro.png"),
        sectionChapter: require("@/assets/img/course-detail/section-chapter.png"),
        sectionWare: require("@/assets/img/course-detail/section-ware.png"),
        sectionMember: require("@/assets/img/course-detail/section-member.png")
      },
      membersParams: {
        memberName: undefined,
        courseCode: ""
      },
      memberPage: {
        pageNum: 1,
        pageSize: 10
      }
    };
  },
  computed: {
    coverImage() {
      const detail = this.courseDetail || {};
      return (
        detail.courseImage ||
        detail.coverImage ||
        detail.coverUrl ||
        detail.cover ||
        ""
      );
    },
    courseStatus() {
      // 0上架 1下架
      const status = Number(this.courseDetail.status);
      if (status === 0) {
        return { label: "上架", type: "success" };
      }
      return { label: "下架", type: "info" };
    },
    categoryText() {
      const one = this.courseDetail.categoryOne;
      const two = this.courseDetail.categoryTwo;
      if (one && two) return `${one} / ${two}`;
      return one || two || "-";
    },
    pathTooltip() {
      return (
        getDictLabel(DICT_TYPE.LEARNING_PATH, this.courseDetail.learningPath) ||
        "-"
      );
    },
    recommendTooltip() {
      return (
        getDictLabel(
          DICT_TYPE.RECOMMEND_TYPE,
          this.courseDetail.recommendCategory
        ) || "-"
      );
    },
    membersTotal() {
      return this.filteredMembers.length;
    },
    pagedMembers() {
      const start = (this.memberPage.pageNum - 1) * this.memberPage.pageSize;
      return this.filteredMembers.slice(
        start,
        start + this.memberPage.pageSize
      );
    }
  },
  methods: {
    formatDateTime,
    goList() {
      this.$router.replace("/course");
    },
    redirectIfCourseMissing(msg) {
      if (msg) {
        this.$message.warning(msg);
      }
      this.goList();
    },
    isValidCourseData(data) {
      return !!(data && (data.courseId != null || data.courseCode));
    },
    openWareUpload() {
      if (this.$refs.courseWareRef) {
        this.$refs.courseWareRef.wareFormOpen("add", {
          courseCode: this.courseCode
        });
      }
    },
    onChapterLoaded(count) {
      this.chapterCount = count || 0;
    },
    async getInfo() {
      if (!this.courseCode) {
        this.redirectIfCourseMissing("课程不存在");
        return false;
      }
      try {
        this.loading = true;
        const res = await getCourseDetail(this.courseCode);
        const data = (res && res.data) || null;
        if (!this.isValidCourseData(data)) {
          this.redirectIfCourseMissing("课程不存在");
          return false;
        }
        this.courseDetail = data;
        return true;
      } catch (e) {
        this.redirectIfCourseMissing();
        return false;
      } finally {
        this.loading = false;
      }
    },
    async getMembers() {
      const res = await getCourseMembers({
        courseCode: this.courseCode
      });
      this.courseMembers = res.data || [];
      this.applyMemberFilter();
    },
    applyMemberFilter() {
      const keyword = (this.membersParams.memberName || "").trim();
      if (!keyword) {
        this.filteredMembers = [...this.courseMembers];
      } else {
        this.filteredMembers = this.courseMembers.filter(item => {
          const name = item.name || "";
          const code = item.memberCode || item.studentNo || "";
          return name.includes(keyword) || String(code).includes(keyword);
        });
      }
      this.memberPage.pageNum = 1;
    },
    handleMemberSearch() {
      this.applyMemberFilter();
    },
    handleMemberPageChange(page) {
      this.memberPage.pageNum = page;
    },
    async getQuestionBanks() {
      try {
        const courseId = this.courseDetail.courseId;
        if (!courseId) {
          this.questionBanks = [];
          return;
        }
        const [theoryRes, practicalRes] = await Promise.all([
          getCourseQuestionBankList({
            courseId,
            questionBankType: 1
          }),
          getCourseQuestionBankList({
            courseId,
            questionBankType: 2
          })
        ]);
        const theoryList = this.extractQuestionBankList(theoryRes);
        const practicalList = this.extractQuestionBankList(practicalRes);
        this.questionBanks = [...theoryList, ...practicalList];
      } catch (e) {
        this.questionBanks = [];
      }
    },
    extractQuestionBankList(res) {
      const data = res && res.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.rows)) return data.rows;
      return [];
    },
    goAddQuestion(item) {
      const path =
        item.questionBankType == 2
          ? "/question/practical/manage"
          : "/question/theory/manage";
      this.$router.push({
        path,
        query: { id: item.questionBankId }
      });
    },
    async removeQuestionBank(item) {
      try {
        await this.$confirm("确定删除该题库吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning"
        });
        await deleteQuestionBank(item.questionBankId);
        this.$message.success("删除成功");
        this.getQuestionBanks();
      } catch (e) {}
    }
  },
  async created() {
    this.courseCode = this.$route.query.courseCode;
    this.membersParams.courseCode = this.courseCode;
    await ensureDictCache().catch(() => null);
    const ok = await this.getInfo();
    if (!ok) return;
    await Promise.all([this.getMembers(), this.getQuestionBanks()]);
  },
  watch: {
    "$route.query.courseCode"(val) {
      if (val === this.courseCode) return;
      this.courseCode = val;
      this.membersParams.courseCode = val;
      this.getInfo().then(ok => {
        if (!ok) return;
        return Promise.all([this.getMembers(), this.getQuestionBanks()]);
      });
    }
  }
};
</script>

<style scoped lang="less">
@primary: #6366f1;

.course-detail {
  /* 本页主要按钮：渐变实心 primary */
  /deep/ .el-button--primary {
    color: #fff !important;
    background: linear-gradient(45deg, #6398f1 0%, #5c63f6 100%) !important;
    border-color: transparent !important;
    border-radius: 10px !important;
    box-shadow: 0px 3px 8px 0px rgba(99, 152, 241, 0.35) !important;
  }
  /deep/ .el-button--primary:focus,
  /deep/ .el-button--primary:hover {
    color: #fff !important;
    background: linear-gradient(45deg, #7aa8f5 0%, #6e74f8 100%) !important;
    border-color: transparent !important;
    box-shadow: 0px 4px 10px 0px rgba(99, 152, 241, 0.42) !important;
  }
  /deep/ .el-button--primary:active {
    color: #fff !important;
    background: linear-gradient(45deg, #5489e8 0%, #4f55e8 100%) !important;
    border-color: transparent !important;
    box-shadow: 0px 2px 6px 0px rgba(99, 152, 241, 0.3) !important;
  }

  .page-toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 0 0 16px;
    padding: 0;
    background: transparent;
  }

  .page-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .section-card {
    margin-bottom: 16px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.62);
    box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.95),
      0px 2px 8px 0px rgba(0, 0, 0, 0.04),
      0px 8px 32px 0px rgba(99, 102, 241, 0.08);

    /deep/ .el-card__header {
      padding: 8px 20px;
      border-bottom: 1px solid rgba(99, 102, 241, 0.08);
      background: transparent;
    }

    /deep/ .el-card__body {
      padding: 20px;
    }
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .section-title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: #1e1b4b;
    span {
      margin-bottom: 6px;
    }
  }

  .title-icon-img {
    width: 40px;
    height: 40px;
    margin-right: 10px;
    object-fit: contain;
    border-radius: 8px;
    flex-shrink: 0;
  }

  .section-extra {
    font-size: 13px;
    color: #86909c;
    font-weight: 400;
  }

  .course-info {
    display: flex;
    align-items: stretch;
    gap: 24px;
  }

  .course-cover {
    position: relative;
    width: 200px;
    height: 140px;
    flex-shrink: 0;
    border-radius: 14px;
    overflow: hidden;
    background: #eef2ff;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    &__placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: #94a3b8;
      font-size: 14px;

      i {
        font-size: 40px;
      }
    }
  }

  .course-meta {
    flex: 1;
    min-width: 0;

    &__title-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
    }

    &__name {
      margin: 0;
      font-size: 22px;
      font-weight: 600;
      color: #1b294b;
      line-height: 1.3;
    }
  }

  .meta-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px 20px;
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #1e1b4b;
    min-width: 0;
  }

  .meta-label {
    color: #64748b;
    flex-shrink: 0;

    &::after {
      content: "：";
    }
  }

  .meta-value {
    color: #1b294b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;

    &--ellipsis,
    &__text {
      display: block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .course-introduce {
    color: #4e5969;
    font-size: 14px;
    line-height: 1.8;
    overflow-wrap: break-word;
    word-break: break-word;

    &__html {
      /deep/ img {
        max-width: 100%;
        height: auto;
      }

      /deep/ p {
        margin: 0 0 8px;
      }
    }
  }

  .member-filter {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;

    &__input {
      width: 280px;

      /deep/ .el-input__inner {
        border-radius: 12px;
      }
    }
  }

  .member-table {
    /deep/ .el-table td {
      border-bottom: none !important;
    }
  }

  .dept-tag {
    border-radius: 12px;
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.28);
    color: @primary;
  }

  .progress-cell {
    padding-right: 8px;

    /deep/ .el-progress__text {
      font-size: 12px !important;
      color: #64748b;
    }
    /deep/ .el-progress-bar__inner {
      background-color: @primary;
    }
  }

  .member-pagination {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }

  .question-bank-section {
    margin-bottom: 16px;
  }

  .bank-card {
    background: rgba(255, 255, 255, 0.72);
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 16px;
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 2px 12px rgba(99, 102, 241, 0.06);
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    &__title {
      font-size: 15px;
      font-weight: 600;
      color: #1b294b;
      line-height: 1.4;
      margin-bottom: 8px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      min-height: 2.8em;
    }

    &__course {
      font-size: 13px;
      color: #86909c;
      margin-bottom: 16px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      padding: 14px 0;
      border-top: 1px solid rgba(99, 102, 241, 0.08);
      border-bottom: 1px solid rgba(99, 102, 241, 0.08);
      margin-bottom: 16px;
    }

    &__footer {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: auto;
    }
  }

  .stat-item {
    text-align: center;

    .stat-value {
      font-size: 16px;
      font-weight: 600;
      color: #1b294b;
      line-height: 1.3;
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 12px;
      color: #86909c;
    }
  }
}

@media (max-width: 992px) {
  .course-detail {
    .course-info {
      flex-direction: column;
    }

    .course-cover {
      width: 100%;
      max-width: 420px;
    }

    .meta-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .page-toolbar {
      justify-content: flex-end;
      padding-bottom: 12px;
    }

    .page-actions {
      margin-left: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Course/CourseDetail.vue